// components/UploadInvoiceButton.tsx
import React, { useRef, useState, useCallback } from "react";
import { Button } from "@/src/components/ui/button";
import { UploadIcon } from "lucide-react";
import { useToast } from "@/src/components/ui/use-toast";
import { fetch } from "@/src/lib/fetch";

type FileData = {
  id: string;
  url?: string;
  name?: string;
  size?: number;
  [k: string]: unknown;
};

type Props = {
  ownerType: string;
  ownerId?: string;
  onUploaded?: (file: FileData) => void;
  accept?: string;
  children?: React.ReactNode;
};

export function UploadInvoiceButton({
  ownerType,
  ownerId,
  onUploaded,
  accept = "application/pdf,image/*",
  children = "Upload Operator's invoice",
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const pickFile = () => inputRef.current?.click();

  const uploadFile = useCallback(
    async (file: File) => {
      const data = new FormData();
      data.append("file", file);
      data.append("ownerType", ownerType);
      if (ownerId) data.append("ownerId", ownerId);

      const res = await fetch<{ status: "success"; data: NonNullable<FileData> }>("/api/files/upload", "POST", data);

      return res.data;
    },
    [ownerType, ownerId],
  );

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const uploaded = await uploadFile(file);
      toast({ title: "Uploaded", description: `${file.name} uploaded successfully.` });
      onUploaded?.(uploaded);
    } catch (err: any) {
      toast({ title: "Upload failed", description: err?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onChange}
        className="hidden"
      />
      <Button onClick={pickFile} variant="upload" disabled={loading}>
        <UploadIcon size={16} className="black min-w-6 mx-1" />
        {loading ? "Uploading..." : children}
      </Button>
    </>
  );
}
